import {
  AppEventType,
  EventHandlerByEventType,
  EventPayloadByEventType
} from "shared/types/AppEvent"

// A handler's payload type depends on the event type it is registered for, which
// a single map cannot express. Erasing the payload to `never` keeps every
// concrete handler assignable in both directions, so the casts below are the
// whole cost — the exported functions stay fully typed, which is all a caller
// sees.
type ErasedEventHandler = (payload: never) => void

const handlersByEventType = new Map<AppEventType, ErasedEventHandler[]>()

export const emitAppEvent = <T extends AppEventType>(
  eventType: T,
  payload: EventPayloadByEventType<T>
) => {
  // Iterate a copy: a handler that unsubscribes itself or a sibling would
  // otherwise shift the ones still waiting to be called past the cursor.
  handlersByEventType
    .get(eventType)
    ?.slice()
    .forEach(handler => {
      const typedHandler = handler as EventHandlerByEventType<T>
      typedHandler(payload)
    })
}

export const subscribeToAppEvent = <T extends AppEventType>(
  eventType: T,
  handler: EventHandlerByEventType<T>
) => {
  const handlers = handlersByEventType.get(eventType)

  if (handlers) {
    handlers.push(handler as ErasedEventHandler)
  } else {
    handlersByEventType.set(eventType, [handler as ErasedEventHandler])
  }
}

export const unsubscribeFromAppEvent = <T extends AppEventType>(
  eventType: T,
  handler: EventHandlerByEventType<T>
) => {
  const handlers = handlersByEventType.get(eventType)
  if (!handlers) return

  // Remove a single occurrence rather than every match, so subscribing the same
  // handler twice still needs two unsubscribes.
  const index = handlers.indexOf(handler as ErasedEventHandler)
  if (index !== -1) {
    handlers.splice(index, 1)
  }
}
